import { handleApiError } from '../core/apiError';
import axiosInstance from '../core/axiosInstance';

/**
 * Sends a request to the server to create a new building.
 * 
 * This function sends a `POST` request to the `/building/add` endpoint 
 * to create a new building on the server. The 
 * server's response, which includes the list, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the success message.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const createNewBuilding = async (buildingData) => {
  try {
    const response = await axiosInstance.post('/building', buildingData);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};


/**
 * Sends a request to the server to fetch the list of building.
 * 
 * This function sends a `GET` request to the `/building` endpoint 
 * to retrieve the list of buildings from the server. The 
 * server's response, which includes the building list, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the building list.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const fetchBuildingList = async () => {
  try {
    const response = await axiosInstance.get('/building');
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};

/**
 * Sends a request to the server to fetch details of specific building.
 * 
 * This function sends a `GET` request to the `/building/${building_id}` endpoint 
 * to retrieve details of specific building from the server. The 
 * server's response, which includes the details of building, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @param {string} brandId - The id of mobile brand.
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the details of specific building.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const getBuildingDetailsById = async (building_id) => {
  try {
    const response = await axiosInstance.get(`/building/${building_id}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};

/**
 * Sends a request to the server to fetch details of specific building.
 * 
 * This function sends a `PUT` request to the `/building/${building_id}` endpoint 
 * to update details of specific building on the server. The 
 * server's response, which includes the new details of building, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @param {string} brandId - The id of mobile brand.
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the details of specific building.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const updateBuildingDetails = async (building_id, payload) => {
  try {
    const response = await axiosInstance.put(`/building/${building_id}`, payload);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};

/**
 * Sends a request to the server to delete details of specific building.
 * 
 * This function sends a `DELETE` request to the `/building/delete/${building_id}` endpoint 
 * to delete details of specific building from the server. The 
 * server's response, which includes the new details of building, will be returned.
 * It uses the default Axios instance with pre-configured headers and base URL.
 * 
 * @function
 * @param {string} brandId - The id of mobile brand.
 * @returns {Promise<Object>} - A promise that resolves to the server's response data, which includes the details of specific building.
 * @throws {Error} - Throws an error if the request fails.
 * 
 */

export const deleteBuildingById = async (building_id) => {
  try {
    const response = await axiosInstance.delete(`/building/${building_id}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error; // Rethrow to handle it in the component
  }
};